import { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { usePlatformAuth } from "./contexts/AuthContext";
import { UploaderPage } from "./components/UploaderPage";
import { getBrandName } from "./utils/brandUtils";
import { BRAND_NAMES } from "./types";

import { DynamicCheckoutPage } from "./components/DynamicCheckoutPage";
import { SuccessPage } from "./components/SuccessPage";
import { CancelPage } from "./components/CancelPage";

import { SpeechToTextPage } from "./components/SpeechToTextPage";
import { ErrorScreen } from "./components/ErrorScreen";
import { MainLayout } from "./components/MainLayout";
import DaisyUIDemo from "./components/DaisyUIDemo";
import DaisyUITest from "./components/DaisyUITest";
import { PrivacyPolicy } from "./components/PrivacyPolicy";
import { TermsOfService } from "./components/TermsOfService";
import { LoadingScreen } from "./components/LoadingScreen";
import { VideoPage } from "./components/VideoPage";

import { api } from "./services/api";
import { Account, UserRequest } from "./types";

// Login page shown when user is not authenticated
function LoginPage() {
  const auth = usePlatformAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const brandName = getBrandName();
  
  const handleSignIn = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await auth.signIn();
    } catch (err) {
      console.error("Sign in error:", err);
      setError(err instanceof Error ? err.message : 'Sign in failed');
      setIsLoading(false);
    }
  };
  
  if (isLoading) {
    return <LoadingScreen message="Signing in" />;
  }
  
  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-base-200"
      style={{
        backgroundImage: `
          repeating-linear-gradient(
            45deg,
            transparent,
            transparent 10px,
            rgba(255, 255, 255, 0.12) 10px,
            rgba(255, 255, 255, 0.12) 20px
          )
        `
      }}
    >
      <div className="card w-96 bg-base-100 shadow-2xl border border-base-300">
        <div className="card-body items-center text-center py-8">
          <div className="mb-4">
            <div className="w-16 h-16 mx-auto bg-primary/30 rounded-full flex items-center justify-center">
              <svg 
                className="w-8 h-8 text-primary" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" 
                />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-base-content mb-1">{brandName}</h2>
          <p className="text-base-content/70 mb-6">Sign in to continue</p>
          {error && (
            <div className="alert alert-error mb-4 w-full">
              <span className="text-sm">{error}</span>
            </div>
          )}
          <button 
            onClick={handleSignIn}
            className="btn btn-primary btn-wide"
          >
            Sign In
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const auth = usePlatformAuth();
  const [account, setAccount] = useState<Account | null>(null);
  const [loadingAccount, setLoadingAccount] = useState(true);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [newRequest, setNewRequest] = useState<UserRequest | null>(null);

  // 🎨 Initialize theme on mount (before authentication)
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) {
      document.documentElement.setAttribute("data-theme", savedTheme);
      return; // Use saved theme
    }
    
    // Set default theme based on brand
    const brandName = getBrandName();
    let defaultTheme = "light"; // Default for FormSprint Health and Kinetic Notes
    
    if (brandName === BRAND_NAMES.BESTI_HEALTH) {
      defaultTheme = "cupcake";
    } else if (brandName === BRAND_NAMES.KINETIC_SCRIBE) {
      defaultTheme = "emerald";
    }
    
    document.documentElement.setAttribute("data-theme", defaultTheme);
    localStorage.setItem("theme", defaultTheme);
  }, []);

  // 🔄 Fetch Stripe account once authenticated
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    if (account) return; // Don't reload if we already have an account

    let cancelled = false;

    async function loadAccount() {
      try {
        setLoadingAccount(true);
        const connectedId = import.meta.env.VITE_STRIPE_CONNECTED_ACCOUNT_ID;
        if (!connectedId) {
          setAccountError("Missing VITE_STRIPE_CONNECTED_ACCOUNT_ID");
          setLoadingAccount(false);
          return;
        }
        
        // Get ID token from platform-aware auth
        const idToken = await auth.getIdToken();
        if (!idToken) {
          setAccountError("Failed to get authentication token");
          setLoadingAccount(false);
          return;
        }
        
        const acct = await api.getAccount(connectedId, idToken);
        if (!cancelled) {
          setAccount(acct);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setAccountError("Failed to load account");
        }
      } finally {
        if (!cancelled) {
          setLoadingAccount(false);
        }
      }
    }

    loadAccount();
    
    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated]); // Only depend on auth.isAuthenticated, not the entire auth object


  const handleRetryAccountLoad = async () => {
    try {
      setAccountError(null);
      setLoadingAccount(true);
      const connectedId = import.meta.env.VITE_STRIPE_CONNECTED_ACCOUNT_ID;
      if (!connectedId) {
        setAccountError("Missing VITE_STRIPE_CONNECTED_ACCOUNT_ID");
        setLoadingAccount(false);
        return;
      }
      
      const idToken = await auth.getIdToken();
      if (!idToken) {
        setAccountError("Failed to get authentication token");
        setLoadingAccount(false);
        return;
      }
      
      const acct = await api.getAccount(connectedId, idToken);
      setAccount(acct);
    } catch (err) {
      console.error(err);
      setAccountError("Failed to load account");
    } finally {
      setLoadingAccount(false);
    }
  };

  const handleAccountUpdate = (updatedAccount: Account) => {
    setAccount(updatedAccount);
  };

  // 1️⃣ OIDC loading and errors
  if (auth.isLoading) return <LoadingScreen />;
  if (auth.error) {
    return (
      <ErrorScreen
        title="Authentication Error"
        message="We couldn't authenticate your session. Please try logging in again."
        details={auth.error.message}
        showRetry={false}
        showHome={true}
      />
    );
  }

  // 2️⃣ Not signed in → show login page
  if (!auth.isAuthenticated) {
    return <LoginPage />;
  }

  // 3️⃣ Account loading and errors
  if (loadingAccount) return <LoadingScreen />;
  if (accountError) {
    return (
      <ErrorScreen
        title="Account Loading Error"
        message="We couldn't load your account information."
        details={accountError}
        onRetry={handleRetryAccountLoad}
        showRetry={true}
        showHome={true}
      />
    );
  }
  if (!account) {
    return (
      <ErrorScreen
        title="No Account Found"
        message="We couldn't find your account. Please contact support."
        showRetry={true}
        showHome={true}
      />
    );
  }

  // 4️⃣ Render protected routes
  return (
    <Routes>
      <Route
        path="/"
        element={
          <MainLayout 
            newRequest={newRequest} 
            onNewRequest={setNewRequest}
            account={account}
            onAccountUpdate={handleAccountUpdate}
          >
            <UploaderPage 
              account={account} 
              onNewRequest={setNewRequest}
            />
          </MainLayout>
        }
      />
      <Route
        path="/pricing"
        element={
          <MainLayout 
            newRequest={newRequest} 
            onNewRequest={setNewRequest}
            account={account}
            onAccountUpdate={handleAccountUpdate}
          >
            <DynamicCheckoutPage 
              onBack={() => window.history.back()} 
              account={account} 
            />
          </MainLayout>
        }
      />
      <Route
        path="/checkout"
        element={
          <MainLayout 
            newRequest={newRequest} 
            onNewRequest={setNewRequest}
            account={account}
            onAccountUpdate={handleAccountUpdate}
          >
            <DynamicCheckoutPage 
              onBack={() => window.history.back()} 
              account={account} 
            />
          </MainLayout>
        }
      />

      <Route
        path="/success"
        element={
          <MainLayout 
            newRequest={newRequest} 
            onNewRequest={setNewRequest}
            account={account}
            onAccountUpdate={handleAccountUpdate}
          >
            <SuccessPage 
              onContinue={() => (window.location.href = "/")}
              account={account}
            />
          </MainLayout>
        }
      />
      <Route
        path="/cancel"
        element={
          <CancelPage 
            onUpgrade={() => (window.location.href = "/pricing")} 
            onBack={() => (window.location.href = "/")} 
          />
        }
      />
      <Route
        path="/speech-to-text"
        element={
          <SpeechToTextPage />
        }
      />
      <Route
        path="/video"
        element={
          <MainLayout 
            newRequest={newRequest} 
            onNewRequest={setNewRequest}
            account={account}
            onAccountUpdate={handleAccountUpdate}
          >
            <VideoPage 
              account={account} 
              onNewRequest={setNewRequest}
            />
          </MainLayout>
        }
      />
      <Route
        path="/demo"
        element={
          <MainLayout 
            newRequest={newRequest} 
            onNewRequest={setNewRequest}
            account={account}
            onAccountUpdate={handleAccountUpdate}
          >
            <DaisyUIDemo />
          </MainLayout>
        }
      />

      <Route
        path="/test"
        element={<DaisyUITest />}
      />
      <Route
        path="/privacy"
        element={<PrivacyPolicy />}
      />
      <Route
        path="/terms"
        element={<TermsOfService />}
      />
      {/* fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
